import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done';
import EditorRecentIcon from '@atlaskit/icon/glyph/editor/recent';
import { N500, N40, B50, B400, G50, G400, R50, R500 } from '@atlaskit/theme/colors';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useSimpleSearchHighlight } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/index.tsx';
import AsyncIcon from '@atlassian/jira-common-components-async-icon/src/view.tsx';
import { statusCategories } from '@atlassian/jira-common-constants/src/index.tsx';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import HighlightText from '@atlassian/jira-software-highlight-text/src/ui/index.tsx';
import type {
	CalendarDataSourceItem,
	CalendarIssue as CalendarIssueType,
} from '../../../types.tsx';
import messages from './messages.tsx';

const isBeforeToday = (dateOnly: DateOnly) =>
	DateOnly.compare(dateOnly, DateOnly.fromDateAccordingToLocalTime(new Date())) === -1;

const getColorScheme = (
	statusCategoryId: number,
	isPast: boolean,
	formatMessage: IntlShape['formatMessage'],
): {
	backgroundColor: string;
	color: string;
	icon?: ReactNode;
	opacity?: number;
} => {
	const { statusCategoryForId, TODO, IN_PROGRESS, DONE } = statusCategories;
	const statusCategory = statusCategoryForId(statusCategoryId);

	if (statusCategory !== DONE && isPast) {
		return {
			backgroundColor: token('color.background.danger', R50),
			color: token('color.text.danger', R500),
			icon: (
				<EditorRecentIcon
					label={formatMessage(messages.overdueLabel)}
					primaryColor={token('color.icon.danger', R500)}
					size="small"
				/>
			),
		};
	}

	switch (statusCategory) {
		case IN_PROGRESS: {
			return {
				backgroundColor: token('color.background.selected', B50),
				color: token('color.text.brand', B400),
			};
		}
		case DONE: {
			return {
				backgroundColor: token('color.background.success', G50),
				color: token('color.text.success', G400),
				icon: (
					<EditorDoneIcon
						label={formatMessage(messages.doneLabel)}
						primaryColor={token('color.icon.success', G400)}
						size="small"
					/>
				),
				opacity: isPast ? 0.8 : undefined,
			};
		}
		case TODO:
		default: {
			return {
				backgroundColor: token('color.background.accent.gray.subtler', N40),
				color: token('color.text', N500),
			};
		}
	}
};

const empty = () => null;

export type Props = {
	issue: CalendarDataSourceItem<CalendarIssueType>;
	onClick: () => void;
};

export const IssueSummary = ({ summary }: { summary: string }) => {
	const highlight = useSimpleSearchHighlight();

	const shouldHighlight = highlight.length > 0;

	return (
		<IssueSummaryWrapper>
			{shouldHighlight ? <HighlightText highlight={highlight} text={summary} /> : summary}
		</IssueSummaryWrapper>
	);
};

const TEST_ID = 'business-calendar.ui.calendar-view.calendar-issue.event-wrapper';

export const CalendarIssue = ({ issue, onClick }: Props) => {
	const { key, summary, duedate, assignee, iconUrl, statusCategoryId } = issue;
	const { formatMessage } = useIntl();
	const isDraggingAllowed = !isOptimisticIssue(issue.id);
	const tooltipMessage = isOptimisticIssue(issue.id) ? summary : `${key}: ${summary}`;

	const { icon, backgroundColor, color, opacity } = getColorScheme(
		statusCategoryId,
		isBeforeToday(duedate),
		formatMessage,
	);

	return (
		<Tooltip content={tooltipMessage}>
			<EventWrapper
				backgroundColor={backgroundColor}
				color={color}
				data-testid={TEST_ID}
				isDraggingAllowed={isDraggingAllowed}
				onClick={isDraggingAllowed ? onClick : undefined}
				// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
				opacity={opacity}
			>
				<TypeWrapper>
					<AsyncIcon url={iconUrl} fallback={empty} />
				</TypeWrapper>

				<IssueWrapper>
					{key} <IssueSummary summary={summary} />
				</IssueWrapper>

				{Boolean(icon) && <IconWrapper>{icon}</IconWrapper>}

				{assignee != null && (
					<Tooltip content={assignee.displayName ?? null}>
						<AssigneeAvatarWrapper>
							<Avatar
								borderColor="transparent"
								appearance="circle"
								src={assignee.avatarUrl ?? undefined}
								size="xsmall"
							/>
						</AssigneeAvatarWrapper>
					</Tooltip>
				)}
			</EventWrapper>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueWrapper = styled.div({
	flex: 1,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AssigneeAvatarWrapper = styled.div({
	display: 'flex',
	marginLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EventWrapper = styled.button<{
	backgroundColor: string;
	color: string;
	isDraggingAllowed: boolean;
	opacity: number;
}>({
	border: 0,
	width: '100%',
	textAlign: 'left',
	padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
	display: 'inline-flex',
	flexWrap: 'wrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isDraggingAllowed }) => (isDraggingAllowed ? 'inherit' : 'not-allowed'),
	minHeight: '20px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	borderRadius: token('border.radius', '4px'),
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ backgroundColor }) => backgroundColor || 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ color }) => color || 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ opacity }) => opacity || 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueSummaryWrapper = styled.span({
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeWrapper = styled.div({
	height: token('space.200', '16px'),
	paddingRight: token('space.100', '4px'),
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export { EventWrapper };
